import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Box, ToggleButtonGroup, ToggleButton, Typography,
  Select, MenuItem, InputLabel, FormControl, Grid
} from '@mui/material';
import { FormatBold, FormatItalic, RestartAlt } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Bu arayüz, düzenlenebilecek stil özelliklerini tanımlar
interface StyleProps {
  fontSize?: string | number;
  fontWeight?: string | number;
  color?: string;
  fontStyle?: string;
  fontFamily?: string;
  bold?: boolean;
  italic?: boolean;
  backgroundColor?: string;
  hoverBackgroundColor?: string;
}

interface ElementStyleEditorModalProps {
  open: boolean;
  onClose: () => void;
  elementKey: keyof any;
  currentStyles: any; // More flexible type to handle different element types
  onSave: (newStyles: StyleProps) => void;
}

// Element adlarını daha kullanıcı dostu hale getirmek için bir map
const elementFriendlyNames: { [key: string]: string } = {
  productName: 'Product Name',
  sku: 'Stock Code (SKU)',
  price: 'Price',
  stockStatus: 'Stock Status',
  addToCartButton: 'Add to Cart Button',
};


const ElementStyleEditorModal: React.FC<ElementStyleEditorModalProps> = ({ open, onClose, elementKey, currentStyles, onSave }) => {
  const { t } = useTranslation('salesSystem');
  const [styles, setStyles] = useState<StyleProps>({});

  useEffect(() => {
    if (open) {
      if (elementKey === 'addToCartButton') {
        // Sepete ekle butonu için özel stillerle başla
        setStyles({
          backgroundColor: currentStyles.backgroundColor || '#0f172a',
          color: currentStyles.color || '#f8fafc',
          hoverBackgroundColor: currentStyles.hoverBackgroundColor || '#1e293b',
        });
      } else {
        // Diğer elementler için mevcut mantık
        const initialStyles: StyleProps = {
          ...currentStyles,
          bold: currentStyles.fontWeight?.toString().includes('bold') || (typeof currentStyles.fontWeight === 'number' && currentStyles.fontWeight >= 600),
          italic: currentStyles.fontStyle === 'italic',
        };
        setStyles(initialStyles);
      }
    }
  }, [open, currentStyles, elementKey]);

  const handleStyleChange = (property: keyof StyleProps, value: any) => {
    setStyles(prevStyles => ({ ...prevStyles, [property]: value }));
  };
  
  const handleToggleChange = (property: 'bold' | 'italic') => {
    const newValue = !styles[property];
    handleStyleChange(property, newValue);

    if (property === 'bold') {
      handleStyleChange('fontWeight', newValue ? 'bold' : 'normal');
    } else if (property === 'italic') {
      handleStyleChange('fontStyle', newValue ? 'italic' : 'normal');
    }
  };

  const handleSave = () => {
    console.log('🔵 [MODAL] Kaydet butonuna basıldı!');
    console.log('🔵 [MODAL] elementKey:', elementKey);
    console.log('🔵 [MODAL] styles:', styles);
    
    const finalStyles = { ...styles };
    delete finalStyles.bold;
    delete finalStyles.italic;
    
    console.log('🔵 [MODAL] finalStyles:', finalStyles);
    console.log('🔵 [MODAL] onSave fonksiyonu çağrılıyor...');
    
    onSave(finalStyles);
    
    console.log('🔵 [MODAL] onClose çağrılıyor...');
    onClose();
  };

  const resetStyles = () => {
    if (elementKey === 'addToCartButton') {
      setStyles({
        backgroundColor: '#0f172a',
        color: '#f8fafc',
        hoverBackgroundColor: '#1e293b',
      });
    } else {
      setStyles({
        fontSize: '1rem',
        fontWeight: 'normal',
        color: '#000000',
        fontStyle: 'normal',
        fontFamily: 'Arial',
        bold: false,
        italic: false,
      });
    }
  }

  const fontOptions = [
    'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Oswald', 'Raleway', 'Poppins', 'Nunito', 'Merriweather', 'Arial', 'Verdana', 'Times New Roman'
  ];

  const friendlyName = elementFriendlyNames[elementKey.toString()] || 'Element';

  const isButtonElement = elementKey === 'addToCartButton';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('ecommerceElementStyleModal.title', { name: friendlyName })}</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ pt: 2 }}>
          {isButtonElement ? (
            // Sepete Ekle Butonu için özel arayüz
            <>
              {/* Buton Arka Plan Rengi */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{position: 'absolute', top: '-8px'}}>{t('ecommerceElementStyleModal.fields.buttonColor')}</InputLabel>
                  <TextField
                    type="color"
                    value={styles.backgroundColor || '#0f172a'}
                    onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
                    fullWidth
                    sx={{'.MuiInputBase-input': {height: '2.5em'}}}
                  />
                </FormControl>
              </Grid>

              {/* Yazı Rengi */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{position: 'absolute', top: '-8px'}}>{t('ecommerceElementStyleModal.fields.textColor')}</InputLabel>
                  <TextField
                    type="color"
                    value={styles.color || '#f8fafc'}
                    onChange={(e) => handleStyleChange('color', e.target.value)}
                    fullWidth
                    sx={{'.MuiInputBase-input': {height: '2.5em'}}}
                  />
                </FormControl>
              </Grid>

              {/* Hover Rengi */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{position: 'absolute', top: '-8px'}}>{t('ecommerceElementStyleModal.fields.hoverColor')}</InputLabel>
                  <TextField
                    type="color"
                    value={styles.hoverBackgroundColor || '#1e293b'}
                    onChange={(e) => handleStyleChange('hoverBackgroundColor', e.target.value)}
                    fullWidth
                    sx={{'.MuiInputBase-input': {height: '2.5em'}}}
                  />
                </FormControl>
              </Grid>

              {/* Sıfırlama Butonu */}
              <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <Button
                  onClick={resetStyles}
                  startIcon={<RestartAlt />}
                  variant="outlined"
                  color="secondary"
                >
                  {t('ecommerceElementStyleModal.actions.reset')}
                </Button>
              </Grid>
            </>
          ) : (
            // Diğer elementler için mevcut arayüz
            <>
              {/* Yazı Tipi */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                <InputLabel>{t('ecommerceElementStyleModal.fields.fontFamily')}</InputLabel>
                <Select
                  value={styles.fontFamily || 'Arial'}
                  label={t('ecommerceElementStyleModal.fields.fontFamily')}
                  onChange={(e) => handleStyleChange('fontFamily', e.target.value)}
                >
                    {fontOptions.map(font => (
                      <MenuItem key={font} value={font} sx={{ fontFamily: font }}>{font}</MenuItem>
                    ))}
                </Select>
              </FormControl>
              </Grid>

              {/* Yazı Boyutu */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('ecommerceElementStyleModal.fields.fontSize')}
                  type="number"
                  value={parseInt(styles.fontSize?.toString() || '16')}
                  onChange={(e) => handleStyleChange('fontSize', `${e.target.value}px`)}
                  fullWidth
                />
              </Grid>
              
              {/* Renk */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                    <InputLabel sx={{position: 'absolute', top: '-8px'}}>{t('ecommerceElementStyleModal.fields.color')}</InputLabel>
                    <TextField
                        type="color"
                        value={styles.color || '#000000'}
                        onChange={(e) => handleStyleChange('color', e.target.value)}
                        fullWidth
                        sx={{'.MuiInputBase-input': {height: '2.5em'}}}
                    />
                 </FormControl>
              </Grid>

              {/* Stil ve Sıfırlama Butonları */}
              <Grid item xs={12} sm={8} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <ToggleButtonGroup value={(() => {
                      const formats = [];
                      if (styles.bold) formats.push('bold');
                      if (styles.italic) formats.push('italic');
                      return formats;
                  })()}
                  >
                    <ToggleButton value="bold" onClick={() => handleToggleChange('bold')}>
                      <FormatBold />
                    </ToggleButton>
                    <ToggleButton value="italic" onClick={() => handleToggleChange('italic')}>
                      <FormatItalic />
                    </ToggleButton>
                  </ToggleButtonGroup>

                  <Button
                    onClick={resetStyles}
                    startIcon={<RestartAlt />}
                    variant="outlined"
                    color="secondary"
                  >
                    {t('ecommerceElementStyleModal.actions.reset')}
                  </Button>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('ecommerceElementStyleModal.actions.cancel')}</Button>
        <Button onClick={handleSave} variant="contained">{t('ecommerceElementStyleModal.actions.save')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ElementStyleEditorModal; 